import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { ArrowRight, MessageSquareQuote } from 'lucide-react'
import { Container, Section, SectionHeading, PullQuote, Card, Button } from './ui'
import {
  listTestimonials,
  TESTIMONIAL_ROLE_LABELS,
  type Testimonial,
  type TestimonialRole,
} from '../../services/testimonialService'

/* ================================================================================
   Voices — real, consented patient/survivor/community voices surfaced as editorial
   pull-quotes. Honesty rule: if no published testimonials exist, we render a clearly
   labeled placeholder inviting people to share — we NEVER fabricate a named survivor.
   ================================================================================ */

export interface PublishedVoices {
  loading: boolean
  /** Featured testimonials first (isFeatured), then the rest — already filtered to PUBLISHED by the public API. */
  voices: Testimonial[]
}

/**
 * usePublishedTestimonials — shared fetch so the homepage hero quote and the Voices strip
 * don't duplicate logic. The public /testimonials endpoint returns only PUBLISHED records.
 * `limit` caps how many are kept (featured ones first).
 */
export function usePublishedTestimonials(limit = 6): PublishedVoices {
  const [loading, setLoading] = useState(true)
  const [voices, setVoices] = useState<Testimonial[]>([])

  useEffect(() => {
    let active = true
    setLoading(true)
    listTestimonials({ page: 0, size: Math.max(limit, 6) })
      .then((res) => {
        if (!active) return
        const all = res.content ?? []
        // surface featured voices first, then most recent
        const sorted = [...all].sort((a, b) => Number(b.isFeatured ?? 0) - Number(a.isFeatured ?? 0))
        setVoices(sorted.slice(0, limit))
      })
      .catch(() => {
        if (!active) return
        setVoices([])
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [limit])

  return { loading, voices }
}

/**
 * Attribution for a Testimonial. This is a plain (non-hook) function called during the
 * render of components that DO consume useTranslation, so it reads the live i18n instance
 * directly (i18n.t) — the surrounding component re-renders on language change, re-invoking
 * this with the updated language. "Shared anonymously" / "TB journey" come from `common.*`.
 */
export function testimonialAttribution(testimonial: Testimonial): { author: string; role: string } {
  const role = testimonial.authorRole ? TESTIMONIAL_ROLE_LABELS[testimonial.authorRole] : undefined
  if (testimonial.isAnonymous || !testimonial.authorName) {
    return {
      author: i18n.t('common.sharedAnonymously', 'Shared anonymously'),
      role: role ?? i18n.t('common.tbJourney', 'TB journey'),
    }
  }
  return {
    author: testimonial.authorName,
    role: [role, testimonial.location].filter(Boolean).join(' · ') || i18n.t('common.tbJourney', 'TB journey'),
  }
}

/**
 * Voices — a strip of up to `max` real testimonials as PullQuotes. Honest empty state.
 * Use <Voices /> on a page to surface human voices; the homepage hero uses
 * usePublishedTestimonials() directly with a single PullQuote.
 */
export function Voices({
  max = 3,
  showHeading = true,
  className = '',
}: {
  max?: number
  showHeading?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const { loading, voices } = usePublishedTestimonials(max)
  const items = voices.slice(0, max)

  return (
    <Section className={`bg-warm-50 ${className}`} ariaLabel={t('voices.sectionAria')}>
      <Container>
        {showHeading && (
          <SectionHeading
            eyebrow={t('voices.eyebrow')}
            title={t('voices.title')}
            description={t('voices.description')}
          />
        )}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" aria-busy="true">
            {Array.from({ length: max }).map((_, i) => (
              <div key={i} className="card p-8 animate-pulse">
                <div className="h-4 w-1/4 bg-warm-200 rounded mb-4" />
                <div className="h-4 w-full bg-warm-200 rounded mb-2" />
                <div className="h-4 w-5/6 bg-warm-200 rounded mb-2" />
                <div className="h-4 w-2/3 bg-warm-200 rounded" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          /* Honest empty state — never fabricate a named person. */
          <Card className="p-8 bg-warm-50 border-warm-200">
            <div className="flex items-start gap-4">
              <span className="shrink-0 w-12 h-12 rounded-xl bg-warm-100 text-human-600 flex items-center justify-center" aria-hidden="true">
                <MessageSquareQuote className="w-6 h-6" />
              </span>
              <div className="flex-1">
                <h3 className="font-semibold text-snow-900">{t('voices.emptyTitle')}</h3>
                <p className="text-sm text-snow-600 mt-1.5">
                  {t('voices.emptyBody')}
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button to="/stories" variant="outline" size="sm" icon={<ArrowRight className="w-4 h-4" />}>
                    {t('common.shareYourStory')}
                  </Button>
                  <Link to="/stories" className="text-sm font-medium text-human-700 hover:text-human-800 hover:underline self-center">
                    {t('common.browseAllStories')}
                  </Link>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((v) => {
                const { author, role } = testimonialAttribution(v)
                return (
                  <PullQuote
                    key={v.id}
                    quote={v.body}
                    author={author}
                    role={role}
                    to="/stories"
                    linkLabel={t('common.readStory')}
                  />
                )
              })}
            </div>
            <div className="mt-8">
              <Button to="/stories" variant="outline" icon={<ArrowRight className="w-4 h-4" />}>
                {t('common.moreStories')}
              </Button>
            </div>
          </>
        )}
      </Container>
    </Section>
  )
}

export default Voices